import * as React from 'react';
import {Check} from 'lucide-react';
import {cn} from '@matic/lib/utils/cn';

/**
 * Checkbox props
 */
export type CheckboxProps = {
  label?: string;
  description?: string;
  error?: string;
  checked?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'>;

/**
 * Checkbox component.
 *
 * Renders a fully custom box overlaid on a transparent native
 * ``<input type="checkbox">``. The native input owns all behavior
 * (label-click via ``htmlFor``, Tab + Space, focus, screen reader
 * announcement, the ``ChangeEvent`` callers expect). The visible box
 * is a sibling ``<span>`` that styles itself via Tailwind's
 * ``peer-checked:`` / ``peer-focus-visible:`` modifiers — this gives
 * us pixel-perfect control over both the checked and unchecked states,
 * which native ``accent-color`` cannot provide (browsers tint the
 * unchecked outline too, leaking primary color into idle states).
 *
 * Mirrors the visual pattern already used in
 * {@link EligibleAssigneePicker} and Scout's {@link SourcesPicker} —
 * canonicalized here so other consumers don't reinvent it.
 */
export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    {
      className,
      label,
      description,
      error,
      checked,
      onChange,
      id,
      disabled,
      ...props
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const hasError = Boolean(error);

    return (
      <div className="w-full">
        <div className="flex items-start gap-3">
          <span className="relative mt-0.5 inline-flex h-4 w-4 shrink-0">
            <input
              ref={ref}
              id={inputId}
              type="checkbox"
              checked={checked}
              onChange={onChange}
              disabled={disabled}
              className={cn(
                // Transparent overlay — handles all interaction; the
                // sibling span renders the visual.
                'peer absolute inset-0 h-full w-full cursor-pointer opacity-0 disabled:cursor-not-allowed',
                className
              )}
              {...props}
            />
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none flex h-4 w-4 items-center justify-center rounded border transition-colors',
                // Idle border, becomes primary fill when checked. The
                // sibling-state selectors (peer-*) read the input's
                // checked / focus / disabled state and project it onto
                // the visual span.
                'border-neutral-300 bg-white',
                'peer-checked:border-primary-500 peer-checked:bg-primary-500',
                'peer-focus-visible:ring-2 peer-focus-visible:ring-primary-500',
                'peer-disabled:opacity-50',
                hasError && 'border-error-500'
              )}
            >
              <Check
                className={cn(
                  'h-3 w-3 text-white',
                  // The check icon shows only when the input is checked.
                  // Hidden by default; revealed via peer-checked.
                  checked ? 'opacity-100' : 'opacity-0'
                )}
                strokeWidth={3}
                aria-hidden="true"
              />
            </span>
          </span>
          {(label ?? description) && (
            <div className="flex flex-col">
              {label && (
                <label
                  htmlFor={inputId}
                  className={cn(
                    'cursor-pointer text-sm font-medium text-neutral-700',
                    disabled && 'cursor-not-allowed opacity-50'
                  )}
                >
                  {label}
                </label>
              )}
              {description && (
                <p
                  className={cn(
                    'text-sm text-neutral-500',
                    disabled && 'opacity-50'
                  )}
                >
                  {description}
                </p>
              )}
            </div>
          )}
        </div>
        {error && <p className="mt-1 text-sm text-error-600">{error}</p>}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

export default Checkbox;
